import * as React from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import Drawer from "@mui/material/Drawer";
import ToggleColorMode from "./ToggleColorMode";
import { useTheme } from "@mui/system";
import { ExitToApp, Login, Menu as MenuIcon } from "@mui/icons-material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import WbSunnyRoundedIcon from "@mui/icons-material/WbSunnyRounded";
import ModeNightRoundedIcon from "@mui/icons-material/ModeNightRounded";
import { useTranslation } from "react-i18next";
import { LanguageSelector } from "../../../components/LanguageSelector/LanguageSelector";
import { getLoginUrl } from "../../../UrlUtil";
import { Fade } from "@mui/material";

const logoStyle = {
  width: "140px",
  height: "auto",
  cursor: "pointer",
};

function AppAppBar({ mode, toggleColorMode }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [showCTA, setShowCTA] = React.useState(false);
  const theme = useTheme();

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };

  // Handle scroll to show/hide CTA button and logo
  React.useEffect(() => {
    const handleScroll = () => {
      const heroElement = document.getElementById("hero");
      if (heroElement) {
        // Show CTA and hide logo when hero is not fully visible anymore
        setShowCTA(window.scrollY > heroElement.offsetHeight - 100);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Check initial position

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Ensure page starts at top on mount
  React.useEffect(() => {
    // Force scroll to top immediately and after a short delay
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    // Also set it after a brief delay to override any other scroll behavior
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const scrollToSection = (sectionId) => {
    const sectionElement = document.getElementById(sectionId);
    const offset = 128;
    if (sectionElement) {
      const targetScroll = sectionElement.offsetTop - offset;
      sectionElement.scrollIntoView({ behavior: "smooth" });
      window.scrollTo({
        top: targetScroll,
        behavior: "smooth",
      });
      setOpen(false);
    }
  };

  return (
    <div>
      <AppBar
        position="fixed"
        sx={{
          boxShadow: 0,
          bgcolor: "transparent",
          backgroundImage: "none",
          mt: 2,
          top: 0, // Ensure it's positioned at the top
        }}
      >
        <Container maxWidth="lg">
          <Toolbar
            variant="regular"
            sx={(theme) => ({
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexShrink: 0,
              borderRadius: "999px",
              bgcolor:
                theme.palette.mode === "light"
                  ? "rgba(255, 255, 255, 0.4)"
                  : "rgba(0, 0, 0, 0.4)",
              backdropFilter: "blur(24px)",
              maxHeight: 40,
              border: "1px solid",
              borderColor: "divider",
              boxShadow:
                theme.palette.mode === "light"
                  ? `0 0 1px rgba(85, 166, 246, 0.1), 1px 1.5px 2px -1px rgba(85, 166, 246, 0.15), 4px 4px 12px -2.5px rgba(85, 166, 246, 0.15)`
                  : "0 0 1px rgba(2, 31, 59, 0.7), 1px 1.5px 2px -1px rgba(2, 31, 59, 0.65), 4px 4px 12px -2.5px rgba(2, 31, 59, 0.65)",
            })}
          >
            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                alignItems: "center",
                px: 0,
              }}
            >
              {/* CTA Button that appears on scroll */}
              <Fade in={showCTA} timeout={300}>
                <Button
                  variant="contained"
                  size="medium"
                  href="https://calendly.com/partihandel"
                  endIcon={<ArrowForwardIcon sx={{ ml: 0.5 }} />}
                  sx={{
                    display: { xs: "none", md: showCTA ? "flex" : "none" },
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    py: 1,
                    px: 2,
                    borderRadius: "50px",
                    whiteSpace: "nowrap",
                    textTransform: "none",
                    background:
                      "linear-gradient(135deg, #66BB6A 0%, #4CAF50 100%)",
                    boxShadow: "0 4px 12px rgba(76,175,80,0.25)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      boxShadow: "0 6px 16px rgba(76,175,80,0.35)",
                      background:
                        "linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)",
                    },
                  }}
                >
                  {t("startpage.herobutton")}
                </Button>
              </Fade>
              <Fade in={!showCTA} timeout={300}>
                <Box
                  sx={{
                    display: !showCTA ? "flex" : "none",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "40px", // Match toolbar maxHeight
                  }}
                >
                  <img
                    src={
                      theme.palette.mode === "light"
                        ? "https://storage.googleapis.com/pifa.se/partihandel.se_row.png"
                        : "https://storage.googleapis.com/pifa.se/partihandel.se_row_inverted.png"
                    }
                    style={{
                      ...logoStyle,
                      display: "block",
                      verticalAlign: "middle",
                    }}
                    alt="logo of sitemark"
                    onClick={() => {
                      window.scrollTo({ top: 0, behavior: "smooth" });
                      setOpen(false); // stäng mobilmenyn om den är öppen
                    }}
                  />
                </Box>
              </Fade>

              <Box sx={{ display: { xs: "none", md: "flex" } }}>
                <MenuItem
                  onClick={() => scrollToSection("features")}
                  sx={{ py: "6px", px: "12px" }}
                >
                  <Typography variant="body2" color="text.primary">
                    {t("landingpage.topbar.features")}
                  </Typography>
                </MenuItem>
                <MenuItem
                  onClick={() => scrollToSection("highlights")}
                  sx={{ py: "6px", px: "12px" }}
                >
                  <Typography variant="body2" color="text.primary">
                    {t("landingpage.topbar.highlights")}
                  </Typography>
                </MenuItem>
                <MenuItem
                  onClick={() => scrollToSection("faq")}
                  sx={{ py: "6px", px: "12px" }}
                >
                  <Typography variant="body2" color="text.primary">
                    {t("landingpage.topbar.faq")}
                  </Typography>
                </MenuItem>
              </Box>
            </Box>
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                gap: 2,
                alignItems: "center",
                opacity: 1,
              }}
            >
              <LanguageSelector
                color={theme.palette.mode === "light" ? "black" : "white"}
              />
              <ToggleColorMode mode={mode} toggleColorMode={toggleColorMode} />

              <Button
                variant="contained"
                size="large"
                component="a"
                href={getLoginUrl()}
                sx={{
                  background:
                    theme.palette.mode === "light" ? "black" : "white",
                  color: theme.palette.mode === "light" ? "white" : "black",
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "light" ? "white" : "black",
                    color: theme.palette.mode === "light" ? "black" : "white",
                  },
                }}
                endIcon={<ExitToApp />}
              >
                {t("topbar.login")}
              </Button>
            </Box>
            <Box
              sx={{
                display: { sm: "flex", md: "none" },
                justifyContent: "flex-start",
                gap: "1rem",
                flexDirection: "row",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  alignContent: "center",
                  gap: "1rem",
                  flexGrow: 1,
                }}
              >
                <Box>
                  {/* Mobile CTA Button */}
                  <Fade in={showCTA} timeout={300}>
                    <Button
                      variant="contained"
                      size="small"
                      href="https://calendly.com/partihandel"
                      sx={{
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                        fontWeight: 600,
                        lineHeight: "normal",
                        py: 0.5,
                        px: 1.5,
                        borderRadius: "50px",
                        textTransform: "none",
                        background:
                          "linear-gradient(135deg, #66BB6A 0%, #4CAF50 100%)",
                        boxShadow: "0 2px 8px rgba(76,175,80,0.25)",
                        display: showCTA ? "flex" : "none",
                        minWidth: "auto",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)",
                        },
                      }}
                    >
                      {t("startpage.herobutton").split("–")[0].trim()}
                    </Button>
                  </Fade>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "right",
                    alignItems: "center",
                    alignContent: "center",
                    gap: "1rem",
                    flexGrow: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      "& .MuiIconButton-root": {
                        padding: 0,
                      },
                    }}
                  >
                    <LanguageSelector
                      color={theme.palette.mode === "light" ? "black" : "white"}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: { xs: "0.65rem", sm: "0.75rem" },
                        textAlign: "center",
                      }}
                    >
                      {t("topbar.language")}
                    </Typography>
                  </Box>

                  <Button
                    component="a"
                    href={getLoginUrl()}
                    sx={{
                      color: "white",
                      minWidth: 0,
                      padding: 0,
                      whiteSpace: "nowrap",
                      "&:active": {
                        background: "transparent",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <Login />
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: { xs: "0.65rem", sm: "0.75rem" },
                          textAlign: "center",
                        }}
                      >
                        {t("topbar.login")}
                      </Typography>
                    </Box>
                  </Button>

                  <Button
                    variant="text"
                    color="primary"
                    aria-label="menu"
                    onClick={toggleDrawer(true)}
                    sx={{ minWidth: "30px", p: "4px" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <MenuIcon
                        sx={{
                          color:
                            theme.palette.mode === "light" ? "black" : "white",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: { xs: "0.65rem", sm: "0.75rem" },
                          textAlign: "center",
                          color: "white",
                        }}
                      >
                        {t("topbar.menu")}
                      </Typography>
                    </Box>
                  </Button>
                </Box>
              </Box>

              <Drawer anchor="right" open={open} onClose={toggleDrawer(false)}>
                <Box
                  sx={{
                    minWidth: "60dvw",
                    p: 2,
                    backgroundColor: "background.paper",
                    flexGrow: 1,
                  }}
                >
                  <MenuItem>
                    <Button
                      variant="contained"
                      size="large"
                      component="a"
                      href={getLoginUrl()}
                      sx={{
                        background:
                          theme.palette.mode === "light" ? "black" : "white",
                        color:
                          theme.palette.mode === "light" ? "white" : "black",
                        "&:hover": {
                          backgroundColor:
                            theme.palette.mode === "light" ? "white" : "black",
                          color:
                            theme.palette.mode === "light" ? "black" : "white",
                        },
                      }}
                      endIcon={<ExitToApp />}
                    >
                      {t("topbar.login")}
                    </Button>
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={() => scrollToSection("features")}>
                    {t("landingpage.topbar.features")}
                  </MenuItem>
                  <MenuItem onClick={() => scrollToSection("highlights")}>
                    {t("landingpage.topbar.highlights")}
                  </MenuItem>
                  <MenuItem onClick={() => scrollToSection("faq")}>
                    {t("landingpage.topbar.faq")}
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={toggleColorMode}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {mode === "dark" ? (
                        <WbSunnyRoundedIcon fontSize="small" />
                      ) : (
                        <ModeNightRoundedIcon fontSize="small" />
                      )}
                      <Typography variant="body2">
                        {mode === "dark" ? "Light mode" : "Dark mode"}
                      </Typography>
                    </Box>
                  </MenuItem>
                  <Divider />
                  {/* Add CTA in mobile menu when scrolled */}
                  {showCTA && (
                    <>
                      <MenuItem>
                        <Button
                          variant="contained"
                          size="medium"
                          href="https://calendly.com/partihandel"
                          fullWidth
                          sx={{
                            background:
                              "linear-gradient(135deg, #66BB6A 0%, #4CAF50 100%)",
                            color: "white",
                            fontWeight: 600,
                            "&:hover": {
                              background:
                                "linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)",
                            },
                          }}
                        >
                          {t("startpage.herobutton")}
                        </Button>
                      </MenuItem>
                      <Divider />
                    </>
                  )}
                </Box>
              </Drawer>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
    </div>
  );
}

AppAppBar.propTypes = {
  mode: PropTypes.oneOf(["dark", "light"]).isRequired,
  toggleColorMode: PropTypes.func.isRequired,
};

export default AppAppBar;
